/* eslint-disable prettier/prettier */
import { apiService } from './api.service';
import { AxiosProgressEvent } from 'axios';
import { Resource } from '../types/resource';
import { ApiResponse } from '@/types';

export interface CreateResourceWithFilePayload {
	title: string;
	description?: string;
	resourcetype:
		| 'document'
		| 'video'
		| 'link'
		| 'image'
		| 'presentation'
		| 'template';
	category?: string;
	tags?: string[];
	visibility?: 'private' | 'shared' | 'public' | 'restricted';
	candownload?: boolean;
	relatedcourseid?: string;
	relatedlessonid?: string;
}

export interface UpdateResourcePayload {
	title?: string;
	description?: string;
	category?: string;
	tags?: string[];
	visibility?: string;
	candownload?: boolean;
}

class ResourceService {
	async uploadResourceWithFile(
		file: File,
		payload: CreateResourceWithFilePayload,
		onProgress?: (progress: number) => void
	) {
		try {
			const formData = new FormData();

			formData.append('file', file);
			formData.append('title', payload.title);
			formData.append('description', payload.description || '');
			formData.append('resourcetype', payload.resourcetype);
			formData.append('category', payload.category || '');
			formData.append('visibility', payload.visibility || 'private');
			formData.append(
				'candownload',
				String(payload.candownload ?? false)
			);

			if (payload.tags) {
				formData.append('tags', JSON.stringify(payload.tags));
			}

			if (payload.relatedcourseid) {
				formData.append('relatedcourseid', payload.relatedcourseid);
			}

			if (payload.relatedlessonid) {
				formData.append('relatedlessonid', payload.relatedlessonid);
			}

			const response = await apiService.post<{ resourceId: string }>(
				'/resource/upload',
				formData,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					onUploadProgress: (event: AxiosProgressEvent) => {
						if (!event.total) return;

						const percent = Math.round(
							(event.loaded * 100) / event.total
						);

						onProgress?.(percent);
					},
				}
			);

			return response;
		} catch (error: any) {
			console.error(
				'❌ Error uploading resource:',
				error?.response?.data || error.message
			);
			throw error;
		}
	}

	async getResourcesByCourse(courseId: string) {
		try {
			const response = await apiService.get<Resource[]>(
				`resource/course/${courseId}`
			);
			if (!response.success) throw new Error(response.message);
			console.log('Resources by Course Response:', response);
			return response.data;
		} catch (error) {
			console.error('Error fetching resources by course:', error);
			throw error;
		}
	}

	async getResourcesByLesson(lessonId: string) {
		try {
			const response = await apiService.get<Resource[]>(
				`/lesson/resource/lesson/${lessonId}`
			);
			if (!response.success) throw new Error(response.message);
			return response.data;
		} catch (error) {
			console.error('Error fetching resources by lesson:', error);
			throw error;
		}
	}

	// 2. Uploading (Multipart for files, JSON for links)

	// async createLinkResource(data: CreateResourceRequest) {
	//     const response = await apiService.post<Resource>('resources', data);
	//     if (!response.success) throw new Error(response.message);
	//     return response.data;
	// }

	// 3. Downloads
	async getDownloadUrl(resourceId: string) {
		const response = await apiService.get<{
			file: string;
			filename: string;
		}>(`resource/${resourceId}/download`);
		if (!response.success) throw new Error(response.message);
		return response.data;
	}

	/**
	 * Triggers a browser download for a resource
	 */
	async downloadResource(resourceId: string) {
		try {
			const data = await this.getDownloadUrl(resourceId);
			const link = document.createElement('a');
			link.href = data.file; // This is the blobUrl or link from your backend
			link.setAttribute('download', data.filename);
			document.body.appendChild(link);
			link.click();
			link.parentNode?.removeChild(link);

			// Optionally notify backend to increment count
			// await apiService.post(`resources/${resourceId}/download-count`, {});
		} catch (error) {
			console.error('Download failed:', error);
			throw error;
		}
	}

	// 4. Management
	async deleteResource(resourceId: string) {
		const response = await apiService.delete(`resource/${resourceId}`);
		if (!response.success) throw new Error(response.message);
		return response.data;
	}

	//update resource
	async updateResource(
		resourceId: string,
		data: UpdateResourcePayload
	): Promise<ApiResponse<void>> {
		try {
			const response = await apiService.put<void>(
				`/resource/${resourceId}`,
				data
			);

			return response;
		} catch (error: any) {
			console.error('Update Resource Error:', error);

			return {
				status: error?.response?.status || 500,
				data: undefined,
				success: false,
				message:
					error?.response?.data?.message ||
					error.message ||
					'Failed to update resource',
			};
		}
	}
}

export const resourceService = new ResourceService();
